import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowDown, List, ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, FadeIn } from "@/components/latir/Motion";
import { Button } from "@/components/latir/Ui";
import { EditorialText } from "@/components/latir/EditorialText";
import { ArtworkCard } from "@/components/latir/ArtworkCard";
import { AudioPlayer, Accordion } from "@/pages/ArtworkDetail";
import { useArtworkSold } from "@/hooks/useArtworkStatus";
import { useSEO } from "@/hooks/useSEO";
import { salas } from "@/data/salas";
import { useCatalogVersion } from "@/data/catalogLive";
import { artists as CATALOG } from "@/data/artists";

const ease = [0.16, 1, 0.3, 1];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Helper: smooth-scroll to an in-page anchor */
function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* -------------------------------------------------------------
   Placeholder museográfico para salas que aún no tienen imagen
   ------------------------------------------------------------- */
function SalaPlaceholder({ label }) {
  const { t } = useLang();
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden bg-paper-deep border border-line max-h-[200px] sm:max-h-[240px] lg:max-h-[260px]">
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
        <span className="h-px w-10 bg-latir-red/60" />
        <span className="font-ui uppercase tracking-[0.28em] text-[0.66rem] text-ink-mute">
          {t("sobre.image.pending")}
        </span>
        <span className="font-display text-[1.15rem] sm:text-[1.4rem] leading-tight text-ink/85">
          {label}
        </span>
      </div>
      {/* Watermark diagonal muy sutil */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(0,0,0,1) 0 1px, transparent 1px 24px)",
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------
   Galería de montaje — fotografías reales de la sala
   Scroll-snap (swipe móvil) + flechas + contador editorial.
   Proporción original intacta: object-contain, sin recortes.
   ------------------------------------------------------------- */
/* -------------------------------------------------------------
   Mini indicador VENDIDA para las miniaturas de la tira de obras
   ------------------------------------------------------------- */
function SalaObraSoldMark({ slug }) {
  const sold = useArtworkSold(slug);
  const { lang } = useLang();
  if (!sold) return null;
  return (
    <span
      data-testid={`sala-obra-sold-${slug}`}
      className="absolute bottom-0 inset-x-0 z-10 bg-latir-red/95 text-paper font-ui uppercase tracking-[0.14em] text-[0.45rem] leading-none text-center py-[3px]"
    >
      {lang === "en" ? "Sold" : "Vendida"}
    </span>
  );
}

function SalaGallery({ sala }) {
  const { lang } = useLang();
  const isEn = lang === "en";
  const items = sala.gallery || [];
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  // Vista ampliada: bloquear scroll de fondo + cerrar con Escape / navegar con flechas
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((v) => (v === null ? v : Math.min(items.length - 1, v + 1)));
      if (e.key === "ArrowLeft") setLightbox((v) => (v === null ? v : Math.max(0, v - 1)));
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, items.length]);

  const goTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, i));
    track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
  };

  const onTrackScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setIdx(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <div className="mb-5 sm:mb-6" data-testid={`sala-gallery-${sala.id}`}>
      {/* Etiqueta editorial + contador */}
      <div className="flex items-center gap-3 mb-3">
        <span className="h-px w-6 bg-latir-red/60" />
        <span className="font-ui uppercase tracking-[0.22em] text-[0.6rem] text-ink-mute">
          {isEn ? "Installation views" : "Montaje de la sala"}
        </span>
        <span
          className="font-ui tabular-nums text-[0.6rem] text-ink-mute/70 ml-auto"
          data-testid={`sala-gallery-counter-${sala.id}`}
        >
          {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
        </span>
      </div>

      {/* Pista con scroll-snap — swipe nativo en móvil.
          El marco envuelve exactamente cada foto (sin franjas vacías):
          el ancho lo define la orientación (vertical 70% / horizontal 85%). */}
      <div
        ref={trackRef}
        onScroll={onTrackScroll}
        className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth"
      >
        {items.map((g, i) => (
          <figure
            key={g.src}
            className="shrink-0 w-full snap-center flex justify-center"
          >
            <span
              className={`relative block border border-line bg-paper-deep p-1.5 sm:p-2 mx-auto ${
                g.wide ? "w-full max-w-[510px]" : "w-full max-w-[390px]"
              }`}
              data-testid={`sala-gallery-frame-${sala.id}-${i}`}
            >
              <img
                src={g.src}
                srcSet={g.srcSmall ? `${g.srcSmall} 800w, ${g.src} 1200w` : undefined}
                sizes={g.wide ? "(max-width: 768px) 100vw, 510px" : "(max-width: 768px) 100vw, 390px"}
                width={g.width}
                height={g.height}
                alt={(g.altEn && isEn) ? g.altEn : g.alt}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                data-testid={`sala-gallery-img-${sala.id}-${i}`}
                className="block w-full h-auto object-contain cursor-zoom-in"
                onClick={() => setLightbox(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightbox(i); } }}
                aria-label={isEn ? "Open full-screen view" : "Abrir vista a pantalla completa"}
              />

              {/* Flechas sobre la foto — solo si hay varias */}
              {items.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => goTo(idx - 1)}
                    disabled={idx === 0}
                    aria-label={isEn ? "Previous photograph" : "Fotografía anterior"}
                    data-testid={`sala-gallery-prev-${sala.id}`}
                    className="absolute left-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 border border-line bg-paper-deep/95 text-ink hover:text-latir-red hover:border-latir-red transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => goTo(idx + 1)}
                    disabled={idx === items.length - 1}
                    aria-label={isEn ? "Next photograph" : "Fotografía siguiente"}
                    data-testid={`sala-gallery-next-${sala.id}`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 border border-line bg-paper-deep/95 text-ink hover:text-latir-red hover:border-latir-red transition-colors disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </>
              )}
            </span>
          </figure>
        ))}
      </div>

      {/* Puntos de posición */}
      {items.length > 1 && (
        <div className="mt-3 flex justify-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`${isEn ? "Go to photograph" : "Ir a la fotografía"} ${i + 1}`}
              data-testid={`sala-gallery-dot-${sala.id}-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? "w-5 bg-latir-red" : "w-1.5 bg-line hover:bg-ink-mute/50"
              }`}
            />
          ))}
        </div>
      )}

      {/* Pista de interacción */}
      <p
        className="mt-2 text-center font-editorial italic text-[0.78rem] text-ink-mute/80"
        data-testid={`sala-gallery-hint-${sala.id}`}
      >
        {isEn ? "Tap the photograph to view it full screen" : "Toca la fotografía para verla en pantalla completa"}
      </p>

      {/* Vista ampliada — pantalla completa con máximo detalle */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[120] bg-ink/95 backdrop-blur-sm flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-label={isEn ? "Full-screen installation view" : "Vista ampliada del montaje"}
            data-testid={`sala-gallery-lightbox-${sala.id}`}
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={items[lightbox].src}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease }}
              src={items[lightbox].srcFull || items[lightbox].src}
              alt={(items[lightbox].altEn && isEn) ? items[lightbox].altEn : items[lightbox].alt}
              data-testid={`sala-gallery-lightbox-img-${sala.id}`}
              className="max-h-[92vh] max-w-[94vw] w-auto h-auto object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Cerrar */}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label={isEn ? "Close full-screen view" : "Cerrar vista ampliada"}
              data-testid={`sala-gallery-lightbox-close-${sala.id}`}
              className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 border border-paper/25 text-paper hover:text-latir-red hover:border-latir-red transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {/* Navegación entre fotos dentro de la vista ampliada */}
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLightbox((v) => Math.max(0, v - 1)); }}
                  disabled={lightbox === 0}
                  aria-label={isEn ? "Previous photograph" : "Fotografía anterior"}
                  data-testid={`sala-gallery-lightbox-prev-${sala.id}`}
                  className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 border border-paper/25 text-paper hover:text-latir-red hover:border-latir-red transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLightbox((v) => Math.min(items.length - 1, v + 1)); }}
                  disabled={lightbox === items.length - 1}
                  aria-label={isEn ? "Next photograph" : "Fotografía siguiente"}
                  data-testid={`sala-gallery-lightbox-next-${sala.id}`}
                  className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-10 h-10 border border-paper/25 text-paper hover:text-latir-red hover:border-latir-red transition-colors disabled:opacity-30 disabled:pointer-events-none"
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <span
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 font-ui tabular-nums text-[0.62rem] tracking-[0.22em] text-paper/70"
                  data-testid={`sala-gallery-lightbox-counter-${sala.id}`}
                >
                  {String(lightbox + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </span>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* -------------------------------------------------------------
   Índice de recorrido (sticky en desktop, scroll horizontal móvil)
   ------------------------------------------------------------- */
function IndexNav() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(salas[0].id);

  // Observador que detecta la sala visible al hacer scroll
  useEffect(() => {
    const observers = [];
    salas.forEach((s) => {
      const el = document.getElementById(`sala-${s.id}`);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(s.id);
          });
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((io) => io.disconnect());
  }, []);

  return (
    <nav
      aria-label="Recorrido curatorial"
      data-testid="salas-index"
      className="relative"
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="h-px w-8 bg-latir-red/70" />
        <span className="font-ui uppercase tracking-[0.24em] text-[0.66rem] text-ink-mute">
          {t("sobre.index.label")}
        </span>
      </div>

      {/* Grid compacto — 6 col × 2 filas en desktop, scroll horizontal en móvil. */}
      <ol
        className="flex lg:grid gap-2 lg:gap-2.5 lg:grid-cols-6 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-5 px-5 lg:mx-0 lg:px-0 no-scrollbar snap-x snap-mandatory"
        data-testid="salas-index-grid"
      >
        {salas.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id} className="shrink-0 lg:shrink w-[210px] lg:w-auto snap-start">
              <a
                href={`#sala-${s.id}`}
                data-testid={`salas-index-link-${s.id}`}
                aria-current={isActive ? "true" : undefined}
                className={
                  "group flex items-center gap-2.5 rounded-[3px] border px-3 py-2 transition-all duration-200 ease-latir " +
                  (isActive
                    ? "border-latir-red bg-paper-raised"
                    : "border-line bg-paper hover:border-latir-red/60 hover:bg-paper-raised")
                }
              >
                <span
                  className={
                    "font-display text-[0.98rem] leading-none tabular-nums shrink-0 transition-colors duration-200 " +
                    (isActive ? "text-latir-red" : "text-latir-red/85")
                  }
                >
                  {s.number}
                </span>
                <span className="flex-1 min-w-0 font-ui text-[0.74rem] tracking-[0.02em] text-ink leading-snug truncate">
                  {(s.titleEn && lang === "en") ? s.titleEn : s.title}
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* -------------------------------------------------------------
   Audioguía por sala — reutiliza <AudioPlayer /> y el backend TTS
   existente. Comprueba /api/tts/status/sala-<id> para saber si el
   MP3 ya está generado antes de mostrar el reproductor.
   ------------------------------------------------------------- */
function SalaAudioGuide({ sala }) {
  const { t, lang } = useLang();
  const audioSlug = `sala-${sala.id}`;
  const [ready, setReady] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/tts/status/${audioSlug}?lang=${lang}`);
        const d = await r.json();
        if (!alive) return;
        const tr = d?.tracks?.curatorial;
        if (tr?.ready && tr?.audio_url) {
          setAudioUrl(`${BACKEND}${tr.audio_url}`);
          setReady(true);
          if (tr.duration_seconds) {
            const s = Math.round(tr.duration_seconds);
            setDuration(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
          }
        } else {
          setReady(false);
          setAudioUrl(null);
        }
      } catch {
        /* silent */
      }
    })();
    return () => { alive = false; };
  }, [audioSlug, lang]);

  const salaTitle = (sala.titleEn && lang === "en") ? sala.titleEn : sala.title;
  const label = lang === "en"
    ? `Listen to this room · ${salaTitle}`
    : `Escuchar esta sala · ${salaTitle}`;

  return (
    <div className="mt-3 pt-3 border-t border-line" data-testid={`sala-audio-${sala.id}`}>
      {ready && audioUrl ? (
        <>
          {!expanded ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid={`sala-audio-btn-${sala.id}`}
              className="group inline-flex items-center gap-3 font-ui uppercase tracking-[0.2em] text-[0.66rem] text-ink hover:text-latir-red transition-colors"
            >
              <span className="w-7 h-7 rounded-full border border-latir-red text-latir-red flex items-center justify-center group-hover:bg-latir-red group-hover:text-paper-raised transition-colors">
                <svg viewBox="0 0 20 20" className="w-2.5 h-2.5" fill="currentColor" aria-hidden>
                  <path d="M5 3.5v13l11-6.5L5 3.5z" />
                </svg>
              </span>
              <span>{t("sobre.audio.listen")}</span>
              {duration && (
                <span className="text-ink-mute normal-case tracking-normal font-ui text-[0.72rem]">
                  · {duration}
                </span>
              )}
            </button>
          ) : (
            <AudioPlayer src={audioUrl} label={label} />
          )}
        </>
      ) : (
        <p className="label-ui text-ink-mute" data-testid={`sala-audio-pending-${sala.id}`}>
          {t("sobre.audio.soon")}
        </p>
      )}
      <Accordion title={t("sobre.audio.transcript")} testid={`sala-transcript-${sala.id}`}>
        <p className="font-editorial text-[1rem] leading-relaxed text-ink-soft whitespace-pre-line">
          {(sala.audioTextEn && lang === "en") ? sala.audioTextEn : sala.audioText}
        </p>
      </Accordion>
    </div>
  );
}

/* -------------------------------------------------------------
   Un capítulo (sala) — módulo editorial compacto en dos columnas.
   ------------------------------------------------------------- */
function SalaChapter({ sala, prev, next, index = 0 }) {
  const { t, lang } = useLang();
  const isEn = lang === "en";
  const salaTitle = (sala.titleEn && isEn) ? sala.titleEn : sala.title;
  const salaEje = (sala.ejeEn && isEn) ? sala.ejeEn : sala.eje;
  const salaQuestion = (sala.questionEn && isEn) ? sala.questionEn : sala.question;
  const salaBody = (sala.bodyEn && isEn) ? sala.bodyEn : sala.body;
  const salaKicker = (sala.kickerEn && isEn) ? sala.kickerEn : sala.kicker;
  const deepLinkLabel = (sala.deepLink?.labelEn && isEn) ? sala.deepLink.labelEn : sala.deepLink?.label;

  // resolver obras asignadas contra el catálogo real
  const artworks = useMemo(
    () =>
      (sala.artworkSlugs || [])
        .map((slug) => CATALOG.find((a) => a.slug === slug))
        .filter(Boolean),
    [sala.artworkSlugs]
  );

  // Toggle LEER MÁS del texto curatorial
  const [openBody, setOpenBody] = useState(false);
  const bodyParas = (salaBody || "").split("\n\n");
  const hasMoreBody = bodyParas.length > 1;
  const visibleParas = openBody ? bodyParas : bodyParas.slice(0, 1);

  // Alternar posición de la imagen para dar ritmo (par ⇒ derecha, impar ⇒ izquierda)
  const imageRight = index % 2 === 0;

  // Salas con galería de montaje: fotos arriba a ancho completo, texto debajo
  const hasGallery = Array.isArray(sala.gallery) && sala.gallery.length > 0;

  const textCol = (
    <div className={hasGallery ? "lg:col-span-12" : imageRight ? "lg:col-span-7 order-2 lg:order-1" : "lg:col-span-7 order-2 lg:order-2"}>
      {/* Número + eje */}
      <Reveal>
        <div className="flex items-baseline gap-4">
          <span
            className="font-display text-[1.5rem] sm:text-[1.75rem] lg:text-[1.9rem] leading-none text-latir-red tabular-nums"
            data-testid={`sala-number-${sala.id}`}
          >
            {sala.number}
          </span>
          <span className="h-px flex-1 bg-line" />
        </div>
        {sala.kicker && (
          <p className="mt-1.5 label-ui text-ink-mute/80">{salaKicker}</p>
        )}
        <h2 className="mt-1 font-display text-[1.3rem] sm:text-[1.55rem] lg:text-[1.75rem] leading-[1.05] tracking-[-0.02em] text-ink">
          {salaTitle}
        </h2>
        <p className="mt-1 font-ui uppercase tracking-[0.18em] text-[0.62rem] text-ink-soft">
          {salaEje}
        </p>
      </Reveal>

      {/* Pregunta */}
      <Reveal delay={0.05}>
        <p
          className="mt-3 font-editorial italic text-[1rem] sm:text-[1.1rem] lg:text-[1.18rem] leading-[1.35] text-latir-red"
          data-testid={`sala-question-${sala.id}`}
        >
          “{salaQuestion}”
        </p>
      </Reveal>

      {/* Texto curatorial — muestra 1er párrafo, LEER MÁS expande el resto */}
      <Reveal delay={0.1}>
        <div className="mt-3 space-y-2.5 max-w-[60ch]">
          {visibleParas.map((para, i) => (
            <EditorialText key={i} style={{ marginTop: 0, fontSize: "0.94rem", lineHeight: 1.55 }}>
              {para}
            </EditorialText>
          ))}
          {hasMoreBody && (
            <button
              type="button"
              onClick={() => setOpenBody((v) => !v)}
              data-testid={`sala-readmore-${sala.id}`}
              className="mt-1 inline-flex items-center gap-2 font-ui uppercase tracking-[0.22em] text-[0.64rem] text-latir-red hover:text-latir-red-deep transition-colors border-b border-latir-red/30 hover:border-latir-red pb-1"
            >
              {openBody ? t("sobre.readLess") : (isEn ? "Read more" : "Leer más")}
              <span aria-hidden className="text-[0.85rem] leading-none">
                {openBody ? "−" : "+"}
              </span>
            </button>
          )}
        </div>
      </Reveal>

      {/* Audioguía LATIR — compacta */}
      {sala.audioText && (
        <Reveal delay={0.12}>
          <SalaAudioGuide sala={sala} />
        </Reveal>
      )}

      {/* deep-link a ficha específica (No estoy sola / Centro de gravedad) */}
      {sala.deepLink && (
        <Reveal delay={0.16}>
          <div className="mt-5">
            <Link
              to={`/artistas/${sala.deepLink.slug}`}
              data-testid={`sala-deeplink-${sala.id}`}
              className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.22em] text-[0.7rem] text-latir-red hover:text-latir-red-deep transition-colors"
            >
              {deepLinkLabel}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
      )}
    </div>
  );

  const imageCol = (
    <div className={imageRight ? "lg:col-span-5 order-1 lg:order-2" : "lg:col-span-5 order-1 lg:order-1"}>
      <Reveal delay={0.05}>
        {sala.image ? (
          <div
            className="relative w-full aspect-[4/3] overflow-hidden bg-paper-deep max-h-[200px] sm:max-h-[240px] lg:max-h-[260px] lg:sticky lg:top-28"
            data-testid={`sala-image-${sala.id}`}
          >
            <img
              src={sala.image}
              alt={sala.imageAlt}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="lg:sticky lg:top-28">
            <SalaPlaceholder label={salaTitle} />
          </div>
        )}
      </Reveal>
    </div>
  );

  return (
    <section
      id={`sala-${sala.id}`}
      data-testid={`sala-${sala.id}`}
      className="scroll-mt-32 pt-5 sm:pt-6 lg:pt-7 first:pt-1 sm:first:pt-2 pb-2 border-t border-line/60 first:border-0"
    >
      {/* Galería de montaje — ancho completo, arriba del texto curatorial */}
      {hasGallery && (
        <Reveal>
          <SalaGallery sala={sala} />
        </Reveal>
      )}

      {/* Layout dos columnas — más compacto */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-7 items-start">
        {textCol}
        {!hasGallery && imageCol}
      </div>

      {/* Obras de la sala — tira horizontal de miniaturas compactas */}
      <div className="mt-4 sm:mt-5" data-testid={`sala-obras-${sala.id}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-6 bg-latir-red/60" />
          <span className="font-ui uppercase tracking-[0.22em] text-[0.6rem] text-ink-mute">
            {t("sobre.sala.obras")}
          </span>
          <span className="hidden sm:inline font-editorial italic text-[0.78rem] text-ink-mute/80 normal-case tracking-normal">
            · {t("sobre.sala.obras.help")}
          </span>
          <span className="sm:hidden font-editorial italic text-[0.75rem] text-ink-mute/80 normal-case tracking-normal">
            · {t("sobre.sala.obras.helpMobile")}
          </span>
          {artworks.length > 0 && (
            <span className="font-ui tabular-nums text-[0.6rem] text-ink-mute/70 ml-auto">
              {String(artworks.length).padStart(2, "0")}
            </span>
          )}
        </div>

        {artworks.length > 0 ? (
          <ul
            className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 snap-x pb-1"
            data-testid={`sala-obras-strip-${sala.id}`}
          >
            {artworks.map((a) => (
              <li key={a.slug} className="shrink-0 snap-start">
                <Link
                  to={`/artistas/${a.slug}`}
                  aria-label={`${a.name} — ${a.title} · Ver obra`}
                  data-testid={`sala-obra-thumb-${a.slug}`}
                  className="group relative block w-[72px] h-[72px] sm:w-20 sm:h-20 lg:w-[100px] lg:h-[100px] overflow-hidden bg-paper-deep border border-line hover:border-latir-red transition-colors"
                >
                  <img
                    src={a.image}
                    alt={`${a.name} — ${(a.titleEn && isEn) ? a.titleEn : a.title}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <SalaObraSoldMark slug={a.slug} />
                  {/* Overlay hover — sólo desktop */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 hidden lg:flex flex-col justify-center bg-latir-red/92 text-paper-raised px-2.5 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  >
                    <p className="font-ui text-[0.62rem] leading-tight line-clamp-2">
                      {a.name}
                    </p>
                    <p className="font-editorial italic text-[0.66rem] leading-tight mt-0.5 line-clamp-2">
                      {(a.titleEn && isEn) ? a.titleEn : a.title}
                    </p>
                    <p className="mt-1 font-ui uppercase tracking-[0.16em] text-[0.5rem]">
                      {t("sobre.sala.viewwork")} →
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-editorial italic text-[0.9rem] text-ink-mute">
            {t("sobre.sala.obras.empty")}
          </p>
        )}
      </div>

      {/* Navegación entre salas — compacta, con posición NN DE 11 */}
      <FadeIn delay={0.1}>
        <nav
          aria-label={`Navegación entre salas — sala ${sala.number}`}
          data-testid={`sala-nav-${sala.id}`}
          className="mt-4 pt-3 border-t border-line grid grid-cols-3 gap-3 items-center"
        >
          {/* Sala anterior */}
          <div className="justify-self-start">
            {prev ? (
              <a
                href={`#sala-${prev.id}`}
                data-testid={`sala-prev-${sala.id}`}
                className="group inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.6rem] text-ink hover:text-latir-red transition-colors"
              >
                <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
                <span>{t("sobre.sala.prev")}</span>
              </a>
            ) : (
              <span className="font-ui uppercase tracking-[0.2em] text-[0.55rem] text-ink-mute/50">
                {t("sobre.sala.start")}
              </span>
            )}
          </div>

          {/* Posición en el recorrido */}
          <div className="justify-self-center">
            <span
              className="font-ui uppercase tracking-[0.22em] text-[0.6rem] text-latir-red tabular-nums"
              data-testid={`sala-position-${sala.id}`}
            >
              {sala.number} {t("sobre.sala.of")} {String(salas.length).padStart(2, "0")}
            </span>
          </div>

          {/* Sala siguiente */}
          <div className="justify-self-end">
            {next ? (
              <a
                href={`#sala-${next.id}`}
                data-testid={`sala-next-${sala.id}`}
                className="group inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.6rem] text-ink hover:text-latir-red transition-colors"
              >
                <span>{t("sobre.sala.next")}</span>
                <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </a>
            ) : (
              <span className="font-ui uppercase tracking-[0.2em] text-[0.55rem] text-ink-mute/50">
                {t("sobre.sala.end")}
              </span>
            )}
          </div>
        </nav>
      </FadeIn>
    </section>
  );
}

/* -------------------------------------------------------------
   Eje curatorial — resumen visible + acordeón editorial con el
   texto completo. Ancho de lectura acotado (~760px) para mejor
   legibilidad.
   ------------------------------------------------------------- */
function EjeCuratorial() {
  const { t, lang } = useLang();
  const isEn = lang === "en";
  const [open, setOpen] = useState(false);
  const resumen = t("sobre.resumen").split("\n\n");
  const body = t("sobre.body").split("\n\n");

  return (
    <div className="max-w-[760px]" data-testid="eje-curatorial">
      {/* Resumen (siempre visible) */}
      <div className="space-y-4">
        {resumen.map((para, i) => (
          <EditorialText
            key={i}
            style={{ marginTop: 0 }}
            dangerouslySetInnerHTML={{ __html: para }}
          />
        ))}
      </div>

      {/* Toggle acordeón */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="eje-curatorial-full"
        data-testid="eje-curatorial-toggle"
        className="mt-5 inline-flex items-center gap-2 font-ui uppercase tracking-[0.22em] text-[0.68rem] text-latir-red hover:text-latir-red-deep transition-colors border-b border-latir-red/30 hover:border-latir-red pb-1"
      >
        {open ? (isEn ? "Read less" : "Leer menos") : t("sobre.readMore")}
        <span aria-hidden className="ml-1 text-[0.9rem] leading-none">
          {open ? "−" : "+"}
        </span>
      </button>

      {/* Cuerpo completo (colapsable) */}
      <div
        id="eje-curatorial-full"
        className={
          "grid transition-[grid-template-rows,opacity] duration-500 ease-latir " +
          (open ? "grid-rows-[1fr] opacity-100 mt-6" : "grid-rows-[0fr] opacity-0")
        }
      >
        <div className="overflow-hidden">
          <div className="space-y-4 pt-2">
            {body.map((para, i) => (
              <EditorialText
                key={i}
                style={{ marginTop: 0 }}
                dangerouslySetInnerHTML={{ __html: para }}
              />
            ))}
            <EditorialText
              className="text-ink-mute"
              style={{ marginTop: 8, fontSize: "1rem" }}
            >
              {t("sobre.curators")}
            </EditorialText>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   Video del recorrido — protagonista de la primera pantalla.
   Sin autoplay: poster + botón Play; al iniciar, controles
   nativos (volumen + pantalla completa). preload="none" para no
   penalizar la carga de la página ni el SEO.
   ------------------------------------------------------------- */
const RECORRIDO_SRC = {
  es: {
    poster: "/media/recorrido-latir-poster.webp",
    mp4: "/media/recorrido-latir.mp4",
    webm: "/media/recorrido-latir.webm",
    title: "Recorrido LATIR — El placer de vivir",
  },
  en: {
    poster: "/media/recorrido-latir-en-poster.webp",
    mp4: "/media/recorrido-latir-en.mp4",
    webm: "/media/recorrido-latir-en.webm",
    title: "LATIR Virtual Tour — El placer de vivir",
  },
};

function RecorridoVideo() {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  // Idioma del VIDEO — independiente del idioma general del sitio. ES por defecto.
  const [vlang, setVlang] = useState("es");
  const cur = RECORRIDO_SRC[vlang];

  const switchLang = (l) => {
    if (l === vlang) return;
    setVlang(l);
    setStarted(false); // vuelve al poster + botón Play del nuevo idioma
  };

  const handlePlay = () => {
    const v = videoRef.current;
    setStarted(true);
    if (v) v.play();
  };

  return (
    <div>
      {/* Selector de idioma del recorrido virtual (no depende del idioma del sitio) */}
      <div className="mb-4 sm:mb-5 flex items-center justify-center gap-3 flex-wrap" data-testid="recorrido-video-lang">
        <span className="font-ui uppercase tracking-[0.24em] text-[0.6rem] text-ink-mute">
          Idioma / Language
        </span>
        <div className="inline-flex border border-line rounded-[3px] overflow-hidden">
          {[
            { id: "es", label: "Español" },
            { id: "en", label: "English" },
          ].map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => switchLang(o.id)}
              aria-pressed={vlang === o.id}
              data-testid={`recorrido-video-lang-${o.id}`}
              className={`px-4 sm:px-5 py-2 font-ui uppercase tracking-[0.2em] text-[0.62rem] transition-colors duration-300 ${
                vlang === o.id ? "bg-ink text-paper" : "text-ink-mute hover:text-ink"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="relative w-full max-w-[920px] mx-auto overflow-hidden rounded-[4px] border border-line bg-black shadow-[0_18px_50px_-24px_rgba(26,23,18,0.45)]"
        data-testid="recorrido-video-frame"
      >
        <video
          key={vlang}
          ref={videoRef}
          poster={cur.poster}
          preload="none"
          playsInline
          controls={started}
          onPlay={() => setStarted(true)}
          title={cur.title}
          aria-label={t("sobre.video.aria")}
          className="block w-full aspect-video object-cover"
          data-testid="recorrido-video"
        >
          <source src={cur.mp4} type="video/mp4" />
          <source src={cur.webm} type="video/webm" />
        </video>
        {!started && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={t("sobre.video.play")}
          data-testid="recorrido-video-play"
          className="absolute inset-0 flex items-center justify-center group cursor-pointer"
        >
          <span className="flex items-center justify-center w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] rounded-full bg-paper/95 border border-latir-red/40 text-latir-red shadow-[0_10px_36px_-10px_rgba(26,23,18,0.5)] transition-transform duration-500 group-hover:scale-105 group-active:scale-95">
            <Play className="w-7 h-7 sm:w-8 sm:h-8 ml-1" fill="currentColor" strokeWidth={1.2} />
          </span>
        </button>
      )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   Cifras de cierre — visible solo desde el 16 oct 2026 (fin de la
   muestra física). Agregadas, sin datos personales.
   ------------------------------------------------------------- */
const EXHIBITION_ENDED = new Date() >= new Date("2026-10-16T00:00:00-06:00");

function CierreStats() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND}/api/visits/closing-stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => {});
  }, []);
  if (!stats) return null;
  const figures = [
    { n: stats.visitors_reserved, label: t("cierre.visitors") },
    { n: stats.guided_visits, label: t("cierre.visits") },
    { n: CATALOG.length, label: t("cierre.artists") },
    { n: stats.artworks_sold, label: t("cierre.sold") },
  ];
  return (
    <section
      className="mx-auto max-w-[1180px] px-5 md:px-10 mt-14 sm:mt-16"
      data-testid="sobre-cierre-stats"
    >
      <div className="border-t border-line pt-10 sm:pt-14">
        <FadeIn>
          <p className="eyebrow text-latir-red inline-flex items-center gap-3">
            <span className="w-6 h-px bg-latir-red inline-block" />
            {t("cierre.eyebrow")}
          </p>
          <h2 className="mt-4 font-display text-[1.9rem] sm:text-[2.6rem] leading-[1.05] tracking-[-0.01em] text-ink">
            {t("cierre.title")}
          </h2>
        </FadeIn>
        <div className="mt-10 sm:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {figures.map((f, i) => (
            <FadeIn key={f.label} delay={i * 0.08}>
              <p
                className="font-display text-[2.6rem] sm:text-[3.4rem] leading-none text-ink"
                data-testid={`cierre-figure-${i}`}
              >
                {f.n}
              </p>
              <p className="mt-3 font-ui uppercase tracking-[0.2em] text-[0.66rem] text-ink-mute">
                {f.label}
              </p>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------
   Página completa — LA MUESTRA como recorrido digital
   ------------------------------------------------------------- */
export default function SobreLatir() {
  useCatalogVersion();
  useSEO({
    title: "LATIR: El placer de vivir | Arteconciencia El Salvador",
    description:
      "LATIR: El placer de vivir es una exposición de Arteconciencia en el Palacio Nacional de El Salvador que reúne arte contemporáneo, reflexión, cuidado y concientización.",
    canonical: "/latir",
  });

  const { t, lang } = useLang();
  const isEn = lang === "en";

  return (
    <div className="pb-24" data-testid="page-sobre">
      {/* ===================== HERO — Video del recorrido ===================== */}
      <section
        className="relative w-full bg-paper"
        data-testid="sobre-hero-video-section"
      >
        <div className="max-w-[1180px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-10 sm:pb-12">
          <div className="text-center max-w-[640px] mx-auto">
            <h1 className="sr-only">
              LATIR: El placer de vivir — exposición de arte contemporáneo de Arteconciencia en el Palacio Nacional de El Salvador
            </h1>
            <motion.img
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease }}
              src="/brand/latir-logo-official.webp"
              alt="LATIR"
              className="w-[128px] sm:w-[160px] lg:w-[184px] h-auto mx-auto"
              data-testid="sobre-hero-logo"
            />
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease }}
              className="mt-3 font-ui italic uppercase tracking-[0.32em] leading-[1.4] text-[0.66rem] sm:text-[0.76rem] text-latir-red"
              data-testid="sobre-hero-phrase"
            >
              {t("home.phrase")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease }}
              className="mt-8 sm:mt-10"
            >
              <p className="eyebrow text-ink-mute inline-flex items-center gap-3">
                <span className="w-6 h-px bg-latir-red inline-block" />
                {t("sobre.video.kicker")}
                <span className="w-6 h-px bg-latir-red inline-block" />
              </p>
              <p
                className="mt-3 font-editorial text-[1.05rem] sm:text-[1.15rem] leading-[1.5] text-ink-soft"
                data-testid="sobre-video-invite"
              >
                {t("sobre.video.text")}
              </p>
            </motion.div>
          </div>

          <FadeIn delay={0.3}>
            <div className="mt-7 sm:mt-9">
              <RecorridoVideo />
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-8 sm:mt-10 text-center">
              <button
                type="button"
                onClick={() => scrollToId("sala-el-placer-de-estar-aqui")}
                data-testid="sobre-comenzar-recorrido"
                className="group inline-flex items-center gap-3 font-ui uppercase tracking-[0.22em] text-[0.78rem] text-latir-red hover:text-latir-red-deep transition-colors border border-latir-red/50 hover:border-latir-red rounded-[3px] px-7 py-3.5"
              >
                {t("sobre.begin")}
                <ArrowDown className="w-4 h-4 transition-transform duration-500 group-hover:translate-y-1" strokeWidth={1.6} />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===================== APERTURA CURATORIAL ===================== */}
      <section className="pt-8 sm:pt-10 md:pt-12">
        <div className="mx-auto max-w-[1180px] px-5 md:px-10">
          <span className="eyebrow text-ink-mute inline-flex items-center gap-3 mb-6">
            <span className="w-6 h-px bg-latir-red inline-block" />
            {t("sobre.eyebrow")}
          </span>

          {/* Tesis principal — el título "LATIR — El placer de vivir" ya
              está integrado en el hero de arriba y no se repite aquí. */}
          <Reveal delay={0.05}>
            <p
              className="font-editorial text-[1.35rem] sm:text-[1.7rem] lg:text-[1.95rem] leading-[1.4] text-ink max-w-[44ch]"
              data-testid="sobre-tesis"
            >
              {t("sobre.tesis")}
            </p>
          </Reveal>

          {/* Texto curatorial general (Eje curatorial) */}
          <div
            className="mt-12 sm:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start"
            data-testid="sobre-eje-curatorial"
          >
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-latir-red/70" />
                <span className="font-ui uppercase tracking-[0.24em] text-[0.68rem] text-ink-mute">
                  {t("sobre.title")}
                </span>
              </div>
            </div>
            <div className="lg:col-span-9">
              <Reveal delay={0.05}>
                <EjeCuratorial />
              </Reveal>
            </div>
          </div>

          {/* Acceso al recorrido digital */}
          <FadeIn delay={0.15}>
            <div className="mt-10 sm:mt-12">
              <button
                type="button"
                onClick={() => scrollToId("recorrido")}
                data-testid="sobre-recorrer"
                className="group inline-flex items-center gap-3 font-ui uppercase tracking-[0.22em] text-[0.78rem] text-latir-red hover:text-latir-red-deep transition-colors"
              >
                {t("sobre.begin")}
                <ArrowDown className="w-4 h-4 transition-transform duration-500 group-hover:translate-y-1" strokeWidth={1.6} />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===================== ÍNDICE + 11 SALAS ===================== */}
      <div id="recorrido" data-testid="recorrido-anchor" className="mx-auto max-w-[1180px] px-5 md:px-10 mt-8 sm:mt-10 lg:mt-12 scroll-mt-28">
        <IndexNav />
        {salas.map((s, i) => (
          <SalaChapter
            key={s.id}
            sala={s}
            prev={salas[i - 1]}
            next={salas[i + 1]}
            index={i}
          />
        ))}
      </div>

      {/* ===================== CIERRE / VISITA FÍSICA ===================== */}
      <section
        className="mx-auto max-w-[1180px] px-5 md:px-10 mt-14 sm:mt-16"
        data-testid="sobre-cierre"
      >
        <div className="border-t border-line pt-8 sm:pt-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
            {/* Imagen — acompañamiento visual, no protagonista */}
            <div className="md:col-span-4 order-1">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 1.1, ease }}
                className="relative w-full aspect-[4/3] overflow-hidden bg-paper-deep max-h-[240px]"
              >
                <img
                  src="/brand/sala-palacio.webp"
                  alt="Interior del Palacio Nacional de El Salvador"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
              </motion.div>
              <p className="label-ui mt-3">{t("footer.venue")}</p>
            </div>

            {/* Texto + CTA */}
            <div className="md:col-span-8 order-2">
              <Reveal>
                <p className="font-display text-[1.55rem] sm:text-[1.9rem] lg:text-[2.2rem] leading-[1.1] tracking-[-0.02em] text-ink max-w-[26ch]">
                  {t("sobre.visit.title")}
                </p>
              </Reveal>
              <FadeIn delay={0.1}>
                <div className="mt-6 sm:mt-7">
                  <Button
                    to="/reservas"
                    variant="primary"
                    arrow="right"
                    data-testid="sobre-reservar"
                  >
                    {t("sobre.visit.cta")}
                  </Button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {EXHIBITION_ENDED && <CierreStats />}
    </div>
  );
}
